import { useNavigate } from "react-router";
import type { InitializeLoadResponse, LoadFilterOptions } from "@generated/import-openapi.gen";
import { useMutation } from "@tanstack/react-query";

import type { PaginationParams, StreamResource } from "@prequel/import-react";
import { useImportContext } from "@prequel/import-react";

import { importClient } from "./sdk/client";

import { useAllPages } from "@/api/pagination";
import { useToast } from "@/providers/ToastProvider";

const routes = {
  delete: (id: string) => `/import/streams/${id}`,
  load: (id: string) => `/import/streams/${id}/load`,
};

const keys = { all: ["import", "streams"] as const };

export function useAllStreams(datasetId?: string) {
  const { streams } = useImportContext();

  return useAllPages<StreamResource>([...keys.all, datasetId ?? ""], (params: PaginationParams) =>
    streams.list({ ...params, dataset_id: datasetId }),
  );
}

export function useDeleteStream() {
  const navigate = useNavigate();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (id: string) => importClient.delete(routes.delete(id)),
    onSuccess: () => {
      addToast({ kind: "SUCCESS", message: "Stream deleted successfully!" });
      navigate("/import/streams");
    },
    // no onError: QueryProvider's mutationCache raises the error toast globally
  });
}

export function useRunLoad() {
  const { addToast } = useToast();

  return useMutation({
    mutationFn: ({ id, filter }: { id: string; filter?: LoadFilterOptions }) =>
      importClient.post<InitializeLoadResponse>(routes.load(id), filter ? { filter } : {}).then((data) => data.load),
    onSuccess: () => {
      addToast({ kind: "SUCCESS", message: "Load submitted." });
    },
    // no onError: QueryProvider's mutationCache raises the error toast globally
  });
}
